import { Button } from '@navi/web-ui/lib/primitives';
import { useForm, Controller } from 'react-hook-form';
import DateTimePickerComponent from '@cp/src/components/DateTimePicker/DateTimePickerComponent';
import { DATE_TIME_TYPE } from '@cp/src/components/DateTimePicker/constants';
import {
  AddAddressFormValues,
  defaultValues,
  primarySource,
  SOURCE_OF_ADDRESS_OPTIONS
} from './contants';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import Dropdown from '@cp/src/components/Dropdown';
import Typography from '@primitives/Typography';
import { SelectPickerOptionProps } from '@cp/src/components/Dropdown/interfaces';
import AddressFormTextInput from './AddressFormTextInput';
import AddressProofUpload from './AddressProofUpload';
import { checkValidPinCode, validatePincode } from '../../constants/utils';
import { addAddress, fetchGeolocations, getPinCodeDetails } from '../../actions/addressesActions';
import { fetchTopAddresses } from '../../actions/topAddressesActions';
import { VALIDATION_MESSAGE } from '../../constants';
import { useParams } from 'react-router-dom';
import { IAddNewAddressFormProps } from './interfaces';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';

const AddNewAddressForm = (props: IAddNewAddressFormProps) => {
  const { loanId, handleAddAddressModal } = props;
  const {
    watch,
    handleSubmit,
    control,
    getValues,
    setValue,
    setError,
    formState: { errors, isValid }
  } = useForm<AddAddressFormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const dispatch = useDispatch();
  const [isPincodeLoading, setIsPincodeLoading] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
  const [proofOfSourceFile, setProofOfSourceFile] = useState<File | null>(null);
  const [proofOfSourceError, setProofOfSourceError] = useState<string>('');

  const handlePinCodeChange = (pinCode: string, onChange: (...event: any[]) => void) => {
    if (checkValidPinCode(pinCode)) {
      setIsPincodeLoading(true);
      getPinCodeDetails(pinCode)
        .then(res => {
          if (res?.data?.city) {
            setValue('city', res?.data?.city);
          } else {
            setValue('city', '');
          }
        })
        .finally(() => {
          setIsPincodeLoading(false);
        });
    } else {
      setValue('city', '');
    }
    onChange(pinCode);
  };
  const { customerId = '', caseBusinessVertical = '', caseReferenceId = '' } = useParams();

  const handleSourceOfAddressChange =
    (onChange: (...event: any[]) => void) => (option: SelectPickerOptionProps) =>
      onChange(option.value);

  const handleAddressFormSubmission = () => {
    handleAddAddressModal();
    dispatch(fetchTopAddresses(loanId, customerId, caseReferenceId));
  };

  const addNewAddress = () => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_ADD_NEW_ADDRESS, {
      customerReferenceId: customerId,
      caseId: caseReferenceId
    });
    const commonPayload = {
      loanAccountNumber: loanId,
      customerReferenceId: customerId,
      caseReferenceId,
      caseBusinessVertical
    };

    // Create FormData for multipart/form-data submission
    const formData = new FormData();

    // Append file if it exists
    if (proofOfSourceFile) {
      formData.append('proofOfSource', proofOfSourceFile);
    }

    // Bundle address fields into addressData JSON object
    const recentActivityDate = getValues('recentActivityDate');
    const addressDataObject = {
      loanAccountNumber: commonPayload.loanAccountNumber,
      lineOne: getValues('lineOne'),
      lineTwo: getValues('lineTwo'),
      city: getValues('city'),
      pinCode: getValues('pinCode'),
      source: primarySource,
      secondarySource: getValues('sourceOfAddress'),
      recentActivityDate: recentActivityDate || null,
      latitude: '',
      longitude: '',
      label: 'HOME'
    };

    formData.append('addressData', JSON.stringify(addressDataObject));

    dispatch(
      addAddress(formData, commonPayload, setIsSubmitButtonDisabled, handleAddressFormSubmission)
    );
  };

  return (
    <div className="flex flex-col">
      <form>
        <div className="flex flex-col">
          <AddressFormTextInput
            control={control}
            name="lineOne"
            rules={{
              required: true,
              minLength: {
                value: 5,
                message: VALIDATION_MESSAGE.ADDRESS_LENGTH_VALIDATION_MESSAGE
              },
              pattern: {
                value: /^(?!\s*$)[a-zA-Z0-9\s,.-]+$/,
                message: VALIDATION_MESSAGE.ADDRESS_VALIDATION_MESSAGE
              }
            }}
            errors={errors}
            inputLabel="House, flat, building"
            placeholder="Eg. A-101"
            validationMessage={VALIDATION_MESSAGE.ADDRESS_VALIDATION_MESSAGE}
            showRequiredIcon
          />
          <AddressFormTextInput
            control={control}
            name="lineTwo"
            rules={{
              required: true,
              minLength: {
                value: 5,
                message: VALIDATION_MESSAGE.ADDRESS_LENGTH_VALIDATION_MESSAGE
              },
              pattern: {
                value: /^(?!\s*$)[a-zA-Z0-9\s,.-]+$/,
                message: VALIDATION_MESSAGE.ADDRESS_VALIDATION_MESSAGE
              }
            }}
            errors={errors}
            inputLabel="Street, locality, colony"
            placeholder="Eg. Chandni Chowk"
            validationMessage={VALIDATION_MESSAGE.ADDRESS_VALIDATION_MESSAGE}
            showRequiredIcon
            containerClass="mt-5"
          />
          <AddressFormTextInput
            control={control}
            name="pinCode"
            rules={{
              required: true,
              minLength: { value: 6, message: VALIDATION_MESSAGE.PINCODE_VALIDATION_MESSAGE },
              maxLength: { value: 6, message: VALIDATION_MESSAGE.PINCODE_VALIDATION_MESSAGE },
              pattern: {
                value: /^[0-9]{6}$/,
                message: VALIDATION_MESSAGE.PINCODE_VALIDATION_MESSAGE
              },
              validate: (value: string) => {
                if (watch('pinCode')) {
                  return validatePincode(value);
                }
              }
            }}
            errors={errors}
            inputLabel="Pincode"
            placeholder="Eg. 110006"
            validationMessage={VALIDATION_MESSAGE.PINCODE_VALIDATION_MESSAGE}
            showRequiredIcon
            containerClass="mt-5"
            handlePincodeChange={handlePinCodeChange}
            isPincodeLoading={isPincodeLoading}
          />
          <AddressFormTextInput
            control={control}
            name="city"
            errors={errors}
            inputLabel="City"
            placeholder="Enter pincode to update city"
            validationMessage=""
            containerClass="mt-5"
            isEditDisabled={true}
          />

          <div className="w-full h-px bg-gray-200 mt-5" />

          <div className="mt-5">
            <div className="flex gap-1 items-center mb-2">
              <span className="text-sm text-gray-600">Source of address</span>
              <span className="text-sm text-red-500">*</span>
            </div>
            <Controller
              control={control}
              name="sourceOfAddress"
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  options={SOURCE_OF_ADDRESS_OPTIONS}
                  selectedValue={value}
                  onSelectionChange={handleSourceOfAddressChange(onChange)}
                  placeholder="Enter or select source"
                  showSolidArrow
                  arrowIconWidth={9}
                  placeholderColor="var(--navi-color-gray-c3)"
                  error={errors.sourceOfAddress ? 'Source of address is required' : false}
                  containerClasses="w-full"
                  tabIndexEnabled
                />
              )}
            />
          </div>

          <div className="mt-5">
            <div className="flex gap-1 items-center mb-2">
              <span className="text-sm text-gray-600">Recent activity date</span>
            </div>
            <Controller
              control={control}
              name="recentActivityDate"
              render={({ field: { onChange, value } }) => (
                <DateTimePickerComponent
                  type={DATE_TIME_TYPE.DATE}
                  value={value}
                  onChange={onChange}
                  iconColor="var(--navi-color-gray-c3)"
                  containerClasses="!w-full"
                />
              )}
            />
          </div>

          <div className="mt-5">
            <div className="flex gap-1 items-center mb-2">
              <span className="text-sm text-gray-600">Upload proof of source</span>
              <span className="text-sm text-red-500">*</span>
            </div>
            <AddressProofUpload
              proofOfSourceFile={proofOfSourceFile}
              setProofOfSourceFile={setProofOfSourceFile}
              setProofOfSourceError={setProofOfSourceError}
              proofOfSourceError={proofOfSourceError}
            />
            {proofOfSourceError && (
              <Typography variant="p5" className="text-red-500 mt-2">
                {proofOfSourceError}
              </Typography>
            )}
          </div>
        </div>
      </form>
      <div className="flex justify-end pt-2">
        <Button
          onClick={handleSubmit(addNewAddress)}
          className="!w-[124px]"
          disabled={!isValid || isSubmitButtonDisabled || !proofOfSourceFile}
        >
          Add
        </Button>
      </div>
    </div>
  );
};

export default AddNewAddressForm;
